import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import ProfaseModal from '../../commons/ProfaseModal';

type WorkflowEmpresaItem = {
  id: number;
  nombre?: string | null;
};

type WorkflowCatalogoItem = {
  id: number;
  nombre?: string | null;
};

type Asignacion = {
  workflowId: number;
  nombre: string;
};

type EmpresaHeaderWorkflowsProps = {
  empresaId: string;
  rfc?: string | null;
};

function workflowConfigConRfc(workflowId: number, rfc?: string | null): string {
  const value = rfc?.trim() ?? '';
  if (!value) return `/workflow/${workflowId}/config`;
  const params = new URLSearchParams();
  params.set('query', value);
  return `/workflow/${workflowId}/config?${params.toString()}`;
}

function workflowLabel(id: number, nombre?: string | null): string {
  const trimmed = nombre?.trim();
  return trimmed || `Workflow #${id}`;
}

export default function EmpresaHeaderWorkflows({ empresaId, rfc }: EmpresaHeaderWorkflowsProps) {
  const [asignados, setAsignados] = useState<Asignacion[]>([]);
  const [listos, setListos] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setListos(false);
    axios
      .get<WorkflowEmpresaItem[]>(`api/empresas/${empresaId}/profase-workflows`)
      .then(res => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const items = rows
          .map(row => {
            if (row.id == null) return null;
            return {
              workflowId: row.id,
              nombre: workflowLabel(row.id, row.nombre),
            };
          })
          .filter((item): item is Asignacion => item != null)
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
        setAsignados(items);
      })
      .catch(() => {
        if (!cancelled) setAsignados([]);
      })
      .finally(() => {
        if (!cancelled) setListos(true);
      });
    return () => {
      cancelled = true;
    };
  }, [empresaId]);

  if (!listos) return null;

  return (
    <>
      <div
        className="d-flex flex-wrap justify-content-start align-items-center"
        style={{
          gap: 6,
          minWidth: 0,
          borderLeft: '1px solid rgba(255,255,255,0.35)',
          paddingLeft: 12,
        }}
        aria-label="Workflows de la empresa"
      >
        {asignados.length === 0 ? (
          <span style={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.3 }}>Sin workflows</span>
        ) : (
          asignados.map(item => (
            <span
              key={item.workflowId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                padding: '2px 8px',
                fontSize: '0.72rem',
                fontWeight: 600,
                lineHeight: 1.3,
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.75)',
              }}
            >
              {item.nombre}
            </span>
          ))
        )}
        <button
          type="button"
          className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
          style={{ width: 28, height: 22, padding: 0 }}
          onClick={() => setOpen(true)}
          aria-label="Ver workflows"
          title="Workflows"
        >
          <FaEye size={12} />
        </button>
      </div>
      <EmpresaWorkflowsModal
        open={open}
        asignados={asignados}
        rfc={rfc}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function EmpresaWorkflowsModal({
  open,
  asignados,
  rfc,
  onClose,
}: {
  open: boolean;
  asignados: Asignacion[];
  rfc?: string | null;
  onClose: () => void;
}) {
  const [catalogo, setCatalogo] = useState<WorkflowCatalogoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const asignadosIds = useMemo(() => {
    const ids = new Set<number>();
    asignados.forEach(item => ids.add(item.workflowId));
    return ids;
  }, [asignados]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get<WorkflowCatalogoItem[]>('api/profase-workflows', {
        params: { query: '%', page: 0, size: 1000, sort: 'nombre,asc' },
      })
      .then(res => {
        if (cancelled) return;
        const items = Array.isArray(res.data) ? res.data : [];
        setCatalogo(
          items
            .filter(item => item.id != null)
            .slice()
            .sort((a, b) => workflowLabel(a.id, a.nombre).localeCompare(workflowLabel(b.id, b.nombre), 'es')),
        );
      })
      .catch(() => {
        if (!cancelled) setCatalogo([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <ProfaseModal open={open} titleId="empresa-header-workflows-title" size="compact" scroll>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <h2 id="empresa-header-workflows-title" className="profase-modal-title m-0">
          <FaEye style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Workflows
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <div
        role="list"
        style={{
          maxHeight: 'min(420px, 55vh)',
          overflow: 'auto',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
        }}
      >
        {loading ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--crm-text-muted)', fontSize: 13 }}>
            Cargando…
          </div>
        ) : catalogo.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--crm-text-muted)', fontSize: 13, fontStyle: 'italic' }}>
            Sin workflows
          </div>
        ) : (
          catalogo.map(item => {
            const asignado = asignadosIds.has(item.id);
            const nombre = workflowLabel(item.id, item.nombre);
            return (
              <div
                key={item.id}
                role="listitem"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  borderBottom: '1px solid var(--crm-border)',
                  fontSize: 13,
                  color: 'var(--crm-text-primary)',
                }}
              >
                <input
                  type="checkbox"
                  checked={asignado}
                  readOnly
                  disabled
                  tabIndex={-1}
                  aria-label={nombre}
                  style={{ pointerEvents: 'none' }}
                />
                <Link
                  to={workflowConfigConRfc(item.id, rfc)}
                  style={{ minWidth: 0, fontWeight: 600 }}
                >
                  {nombre}
                </Link>
              </div>
            );
          })
        )}
      </div>
    </ProfaseModal>
  );
}
